import React from 'react';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink } from "react-router-dom";
import { Box, Grid, IconButton, TextField, Typography } from '@mui/material';

import { PATHS } from '../type/EnumPaths';
import { formatTotal } from '../util/formatTotal';
import { formatPrice } from '../util/formatPrice';
import { formatTotalItem } from '../util/formatTotalItem';
import { IShoppingCartItems } from '../type/IShoppingCartItems';
import { handleCartChangeQty } from '../util/handleCartChangeQty';
import { handleCartDeleteItem } from '../util/handleCartDeleteItem';
import { ProductContext, ShoppingCartContext } from '../util/useContext';
import { shoppingCartGetCartItems } from '../util/shoppingCartGetCartItems';

interface ShoppingCartPopupProps {
  open: boolean;
  anchorEl: EventTarget & HTMLSpanElement | null;
  onClose: React.ReactEventHandler<{}>;
}

const ShoppingCartPopup = (props: ShoppingCartPopupProps) => {
  const id = props.open ? 'cartpopup' : undefined;  
  const { shoppingCartString, setShoppingCart } = React.useContext(ShoppingCartContext);
  const { priceList, productMap } = React.useContext(ProductContext);
  const [items, setItems] = React.useState<IShoppingCartItems[]>(shoppingCartGetCartItems(shoppingCartString, productMap));
  const [total, setTotal] = React.useState("");
  const [currentIndex, setCurrentIndex] = React.useState<number>(-1);

  React.useEffect(() => {
    const cartItems = shoppingCartGetCartItems(shoppingCartString, productMap);
    setItems(cartItems);
    setTotal(formatTotal(cartItems, priceList));
  }, [shoppingCartString, productMap, priceList]);


  const renderItems = () => {
    if (shoppingCartString === "{}") return (
      <Grid item xs={12} padding={5}>
        <Typography>
          Check out our <Link to={`/${PATHS.PRODUCTS}`} component={RouterLink}>product</Link> list to fill your cart :)
        </Typography>
      </Grid>
    )
    return (
      <Grid item xs={12} onMouseLeave={() => setCurrentIndex(-1)}>
        {
          items.map((item, index) => {
            const unitPrice = priceList[item.product.id]?.cardPrice || "0";
            return (
              <Box key={`shopping-item-${index}`} onMouseEnter={() => setCurrentIndex(index)}>
                <Grid container sx={{ paddingTop: "8px", paddingBottom: "8px" }}>
                  <Grid item xs={6}>
                    <Typography
                      fontSize="0.9rem"
                      fontWeight={500}
                      padding="0"
                    >
                      {item.product?.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Qty:"
                      InputLabelProps={{
                        sx: {
                          fontSize: "0.7rem"
                        }
                      }}
                      inputProps={{
                        sx: {
                          fontSize: "0.7rem",
                        },
                        min: 0,
                        max: 50,
                        textalign: "center",
                        padding: 0
                      }}
                      size="small"
                      type="number"
                      variant="standard"
                      defaultValue={item.qty}
                      onChange={(e) => handleCartChangeQty(shoppingCartString, item.id, e.target.value, setShoppingCart)}
                    />
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "left"}}>
                    <IconButton
                      aria-label="delete"
                      size="small"
                      sx={{
                        visibility: currentIndex === index ? "visible" : "hidden"
                      }}
                      onClick={() => handleCartDeleteItem(shoppingCartString, item.id, setShoppingCart)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                  <Grid item xs={2} sx={{ textAlign: "right" }}>
                    <Typography fontSize={"0.8rem"}>{"(" + formatPrice(unitPrice) + ")"}</Typography>
                    <Typography fontSize={"0.9rem"}>{formatTotalItem(unitPrice, item.qty)}</Typography>
                  </Grid>
                </Grid>
              </Box>
            )
          })
        }
      </Grid>
    )
  }


  return (
    <Popover
      id={id}
      open={props.open}
      anchorEl={props.anchorEl}
      onClose={props.onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      elevation={4}
      marginThreshold={10}
      className='cart-popup'
      slotProps={{ paper: { sx:{ borderRadius: "25px" }} }}
    >
      <Grid
        container
        maxHeight={"500px"}
        maxWidth={"300px"}
        overflow="scroll"
        margin={"12px"}
      >
        <Grid item xs={8}>
          <Typography fontWeight={800}>Quick Cart</Typography>
          <hr />
        </Grid>
        <Grid item xs={4} textAlign="right">
          <Link key="cart" to={`/${PATHS.CHECKOUT}`} fontWeight={800} component={RouterLink}>Checkout</Link>
          <hr />
        </Grid>
        
        {renderItems()}

        <Grid item xs={12} textAlign="right">
          <hr />
          <Typography fontWeight={800}>Sub total: SGD {total}</Typography>
        </Grid>
      </Grid>
    </Popover>
  )
}

export default React.memo(ShoppingCartPopup);